import type { DetailProcessAdditionalColumn } from '@/pages/processes/detail/[id]/types';
import { javascript } from '@codemirror/lang-javascript';
import { sql } from '@codemirror/lang-sql';
import { StreamLanguage, syntaxTree } from '@codemirror/language';
import { vb } from '@codemirror/legacy-modes/mode/vb';
import { EditorState } from '@codemirror/state';
import { ColumnType } from '@lib/api';

// Define property node types and access symbols for different languages
export const PROPERTY_NODE_TYPES = {
  javascript: {
    propertyTypes: ['PropertyName'],
    isCaseSensitive: true, // JavaScript is case-sensitive
    accessSymbols: '',
    language: () => javascript(),
  },
  csharp: {
    propertyTypes: ['TypeIdentifier', 'PropertyName'],
    isCaseSensitive: true, // C# is case-sensitive
    accessSymbols: 'this.',
    language: () => javascript(), // Temporarily use JavaScript parser as a substitute
  },
  vbscript: {
    propertyTypes: ['variableName'],
    isCaseSensitive: false, // VBScript is case-insensitive
    accessSymbols: 'Me.',
    language: () => StreamLanguage.define(vb),
  },
  odataFilter: {
    propertyTypes: ['PropertyName'],
    isCaseSensitive: true, // VBScript is case-insensitive
    accessSymbols: '',
    language: () => javascript(),
  },
  sql: {
    // Note: parsing type='Brackets' only, e.g. `[[Address]]` or `[[GBL_AUTOID]]`
    // propertyTypes: ['VariableName', 'PropertyName', 'Identifier', 'TypeIdentifier'],
    propertyTypes: ['Brackets'],
    isCaseSensitive: false, // SQL is case-insensitive
    accessSymbols: '',
    filter: (token: string) => /^\[\[.+?\]\]$/.test(token),
    language: () => sql(),
  },
};

interface AttributeMatch {
  from: number;
  to: number;
  attribute: string;
}

// Find custom attributes in code
export function findCustomAttributesInCode(
  customAttributes: string[] | undefined,
  language: keyof typeof PROPERTY_NODE_TYPES,
  state: EditorState,
  from: number,
  to: number,
): AttributeMatch[] {
  if (!language) return [];

  const nodeTypes = PROPERTY_NODE_TYPES[language];
  if (!nodeTypes) return [];

  // Create a new EditorState with specified language
  const languageState = EditorState.create({
    doc: state.doc,
    extensions: [nodeTypes.language()],
  });

  const matches: AttributeMatch[] = [];
  const tree = syntaxTree(languageState);

  // Iterate through the syntax tree
  tree.iterate({
    from,
    to,
    enter: (node) => {
      // Get node text
      const nodeText = state.doc.sliceString(node.from, node.to);
      if (!nodeText) return;

      // fix language of SQL: the accessSymbols declared for SQL is empty
      // and should be allowed
      // if (!nodeTypes.accessSymbols) return

      // Get node type name
      const nodeType = node.type.name;

      if (!nodeTypes.propertyTypes.includes(nodeType)) {
        return;
      }

      // Check if the attribute matches any custom attributes
      let matchingAttr: string | undefined;
      if (customAttributes) {
        matchingAttr = customAttributes.find((attr) =>
          nodeTypes.isCaseSensitive
            ? nodeText === attr || nodeText === `.${attr}`
            : nodeText.toLowerCase() === attr.toLowerCase() ||
              nodeText.toLowerCase() === `.${attr.toLowerCase()}`,
        );
      }

      if (!matchingAttr) {
        matchingAttr = nodeText.startsWith('.') ? nodeText.slice(1) : nodeText;
      }

      const nodeTextStartWithDot = nodeText.startsWith('.');

      // Check if previous text contains access symbol
      const prevText =
        node.from > nodeTypes.accessSymbols.length
          ? state.doc.sliceString(
              node.from - nodeTypes.accessSymbols.length + (nodeTextStartWithDot ? 1 : 0),
              node.from + (nodeTextStartWithDot ? 1 : 0),
            )
          : '';

      // added ability to filter expected tokens [[*]] for SQL
      if (nodeTypes['filter'] && !nodeTypes['filter'](matchingAttr)) {
        return;
      }
      if (prevText === nodeTypes.accessSymbols) {
        matches.push({
          from: node.from,
          to: node.to,
          attribute: matchingAttr,
        });
      }
    },
  });

  return matches;
}

/**
 * Find custom attributes in a code string
 * @param code The code string to analyze
 * @param language The programming language of the code
 * @param customAttributes List of custom attributes to find
 * @returns List of matched attributes with their positions
 */
export function findCustomAttributesInString(
  code: string,
  language: keyof typeof PROPERTY_NODE_TYPES,
  customAttributes?: string[],
): AttributeMatch[] {
  if (!code || !language) return [];

  const nodeTypes = PROPERTY_NODE_TYPES[language];
  if (!nodeTypes) return [];

  // Create EditorState
  const state = EditorState.create({
    doc: code,
    extensions: [nodeTypes.language()],
  });

  // Process using existing method
  return findCustomAttributesInCode(customAttributes, language, state, 0, code.length);
}

export function getColumnNames(columns: DetailProcessAdditionalColumn[]) {
  return columns.map((item) => {
    if (item.columnTypeDisplayName === ColumnType.Calculation) {
      return `__${item.name}`;
    }
    return `_${item.name}`;
  });
}
